import { Link } from "@/lib/nav";
import type { CSSProperties } from "react";

import EmptyState from "@buildpy/ui/empty-state";
import LinkTabs from "@/components/link-tabs";
import Money from "@/components/money";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import StatusBadge from "@buildpy/ui/status-badge";
import { Button } from "@buildpy/ui/button";
import { cardLabel, RECENT_ONLY_COLUMNS, type BoardCard, type BoardColumn, type BoardColumnKey, type CustomerBoard as Board } from "@buildpy/core/modules/customer/board";

interface Props {
  board: Board;
  // 폰에서 보는 열(?col=).
  col: BoardColumnKey;
  colHrefs: Record<BoardColumnKey, string>;
}

const orderText = (column: BoardColumn): string =>
  RECENT_ONLY_COLUMNS.includes(column.key) ? `${column.expanded ? "전체" : "최근 30일"} · 최근 순` : "오래 머문 순";

// 카드를 덮는 링크라 카드 글자는 링크 이름(cardLabel)으로 읽히고 화면 글자는 보조기기에서 숨긴다 · hover 는 열 바탕(muted)과 다른 accent.
const Card = ({ card }: { card: BoardCard }) => (
  <SectionCard as="li" className="relative flex flex-col gap-y-1.5 hover:bg-accent" data-testid={`board-card-${card.id}`} padding="sm">
    <div className="flex items-baseline justify-between gap-x-2">
      <Link aria-label={cardLabel(card)} className="min-w-0 truncate font-medium after:absolute after:inset-0" href={card.href}>
        {card.name}
      </Link>
      <span aria-hidden className="shrink-0 text-xs text-muted-foreground">
        {card.owner}
      </span>
    </div>
    {card.meta && (
      <p aria-hidden className="truncate text-xs text-muted-foreground">
        {card.meta}
      </p>
    )}
    {card.progress && (
      <div aria-hidden className="flex items-center gap-x-2 text-xs text-muted-foreground">
        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <span className="block h-full w-(--w) rounded-full bg-status-progress" style={{ "--w": `${card.progress.pct}%` } as CSSProperties} />
        </span>
        <span className="shrink-0 tabular-nums">{[card.progress.text, `${card.progress.pct}%`].filter(Boolean).join(" · ")}</span>
      </div>
    )}
    {(card.badges.length > 0 || card.amount !== null) && (
      <div aria-hidden className="flex flex-wrap items-center gap-1.5">
        {card.badges.map((badge) =>
          badge.kind === "status" ? (
            <StatusBadge key={badge.text} label={badge.text} tone={badge.tone} />
          ) : (
            <Pill key={badge.text} tone={badge.tone}>
              {badge.text}
            </Pill>
          )
        )}
        {card.amount !== null && <Money className="ml-auto text-sm font-semibold" value={card.amount} />}
      </div>
    )}
    {card.next && (
      <Button asChild className="relative z-10 w-full" size="sm" variant="outline">
        <Link href={card.next.href}>{card.next.label}</Link>
      </Button>
    )}
  </SectionCard>
);

const Cards = ({ column }: { column: BoardColumn }) => (
  <>
    {column.cards.length > 0 ? (
      <ul className="flex flex-col gap-y-2">
        {column.cards.map((card) => (
          <Card key={card.id} card={card} />
        ))}
      </ul>
    ) : (
      <EmptyState className="py-4">{column.hidden > 0 && !column.expanded ? "최근 30일에는 없습니다" : "없습니다"}</EmptyState>
    )}
    {column.moreHref && (
      <Link className={MORE_LINK} data-testid={`board-more-${column.key}`} href={column.moreHref}>
        더 보기 · 이전 {column.hidden}명
      </Link>
    )}
    {column.lessHref && (
      <Link className={MORE_LINK} data-testid={`board-less-${column.key}`} href={column.lessHref}>
        최근 30일만
      </Link>
    )}
  </>
);

const MORE_LINK = "text-center text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground";

const ColumnHead = ({ column }: { column: BoardColumn }) => (
  <h2 className="flex items-baseline justify-between px-1 text-sm font-semibold">
    <span>
      {column.title}
      {column.expanded && <span className="font-normal text-muted-foreground"> · 전체</span>}
    </span>
    <span className="tabular-nums text-muted-foreground">{column.cards.length}</span>
  </h2>
);

// 고객 보드 — 열 = 단계. 넓은 화면은 열을 가로로(보드 영역만 가로 스크롤), 폰은 열 탭 하나씩. 끌어 옮기기는 없다(단계는 상세의 단계 메뉴로).
const CustomerBoard = ({ board, col, colHrefs }: Props) => {
  const active = board.columns.find((c) => c.key === col) ?? board.columns[0]!;
  const lost = board.columns.find((c) => c.key === "LOST");
  const main = board.columns.filter((c) => c.key !== "LOST");
  return (
    <div className="min-w-0" data-testid="customer-board">
      <div className="flex flex-col gap-y-3 md:hidden" data-testid="customer-board-phone">
        {/* 390 폭에서도 여덟 열(완료·이탈 포함)이 처음부터 보이게 가로로 밀지 않고 줄을 바꾼다. */}
        <LinkTabs
          activeHref={colHrefs[active.key]}
          aria-label="보드 열"
          className="flex-wrap"
          items={board.columns.map((c) => ({ label: `${c.title} ${c.cards.length}`, href: colHrefs[c.key] }))}
        />
        <p className="text-xs text-muted-foreground" data-testid="customer-board-phone-summary">
          {active.title} {active.cards.length}명 · {orderText(active)}
        </p>
        <div className="flex flex-col gap-y-2">
          <Cards column={active} />
        </div>
      </div>

      <div className="hidden overflow-x-auto pb-2 md:block" data-testid="customer-board-scroll">
        <div className="flex w-max items-start gap-x-3">
          {main.map((column) => (
            <section key={column.key} aria-label={column.title} className="flex w-64 shrink-0 flex-col gap-y-2 rounded-lg bg-muted p-2" data-testid={`board-column-${column.key}`}>
              <ColumnHead column={column} />
              <Cards column={column} />
            </section>
          ))}
          {lost && (
            <SectionCard as="details" className="group w-64 shrink-0" data-testid="board-column-LOST" open={lost.expanded} padding="sm">
              <summary className="cursor-pointer text-sm font-semibold">
                {lost.title} {lost.cards.length}
                {lost.expanded && " · 전체"} · <span className="group-open:hidden">펼치기</span>
                <span className="hidden group-open:inline">접기</span>
              </summary>
              <div className="mt-2 flex flex-col gap-y-2">
                <Cards column={lost} />
              </div>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerBoard;
